import React, { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import Select from "react-select";
import {
  IconArrowLeft,
  IconClipboardList,
  IconBuildingWarehouse,
  IconTruck,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconClock,
  IconCalendar,
  IconUser,
  IconReceipt,
  IconPlus,
  IconTrash,
  IconCreditCard,
  IconCash,
  IconInfoCircle,
  IconArrowForward,
  IconSend,
  IconBoxSeam,
  IconPrinter,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import {
  useRequisitionDetail,
  submitRequisitionForApproval,
  cancelRequisition,
  approveRequisition,
  forwardRequisitionToProvider,
  recordRequisitionReceiving,
} from "../controllers/requisition.controller";
import { useSuppliers, useInventoryItems } from "../controllers/inventory.controller";
import { usePaymentTypes, useStoreSettings } from "../controllers/settings.controller";
import { useTheme } from "../contexts/ThemeContext";
import { printRequisitionSlip } from "../helpers/PrinterHelper";
import toast from "react-hot-toast";

const STATUS_CONFIG = {
  draft: { label: "Draft", badge: "badge-ghost" },
  pending_approval: { label: "Pending Approval", badge: "badge-warning" },
  partially_approved: { label: "Partially Approved", badge: "badge-info" },
  approved: { label: "Approved", badge: "badge-primary" },
  rejected: { label: "Rejected", badge: "badge-error" },
  provider_ordered: { label: "Provider Ordered", badge: "badge-secondary" },
  provider_dispatched: { label: "Dispatched by Provider", badge: "badge-accent" },
  receiving: { label: "Receiving", badge: "badge-warning" },
  received: { label: "Received & Confirmed", badge: "badge-success" },
  issued: { label: "Stock Issued", badge: "badge-success" },
  cancelled: { label: "Cancelled", badge: "badge-neutral" },
};

export default function RequisitionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs";

  const { requisition, isLoading, error, mutate } = useRequisitionDetail(id);
  const { data: suppliersData } = useSuppliers();
  const suppliers = Array.isArray(suppliersData) ? suppliersData : suppliersData?.suppliers || [];
  const { data: paymentTypesData } = usePaymentTypes();
  const paymentTypes = useMemo(() => {
    const list = Array.isArray(paymentTypesData)
      ? paymentTypesData
      : Array.isArray(paymentTypesData?.data)
      ? paymentTypesData.data
      : [];
    return list.filter((pt) => pt && pt.is_active !== 0 && pt.is_active !== false && pt.isActive !== false);
  }, [paymentTypesData]);

  const availablePaymentMethods = useMemo(() => {
    const methods = [];
    if (Array.isArray(paymentTypes)) {
      paymentTypes.forEach((pt) => {
        if (!pt) return;
        const title = pt.title || pt.name;
        if (title && !methods.includes(title)) {
          methods.push(title);
        }
      });
    }
    if (methods.length === 0) {
      return ["Cash", "Bank Transfer", "Card", "Cheque / Check", "UPI / QR"];
    }
    if (!methods.some((m) => m && m.toLowerCase() === "cash")) {
      methods.unshift("Cash");
    }
    return methods;
  }, [paymentTypes]);

  // Safe Date & Number Helpers to eliminate Electron / Browser runtime crashes
  const formatDateSafe = (dateStr, includeTime = false) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return includeTime ? d.toLocaleString() : d.toLocaleDateString();
    } catch {
      return String(dateStr);
    }
  };

  const formatNumberSafe = (val, decimals = 2) => {
    const n = Number(val);
    return isNaN(n) ? (0).toFixed(decimals) : n.toFixed(decimals);
  };

  // Modals state
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isReceivingModalOpen, setIsReceivingModalOpen] = useState(false);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  // Approval Form state
  const [approvalNotes, setApprovalNotes] = useState("");
  const [approvalAction, setApprovalAction] = useState("purchase"); // "purchase" (default: add stock on arrival) or "issue" (deduct from pantry now)
  const [itemApprovals, setItemApprovals] = useState([]);

  // Receiving Form state
  const [supplierId, setSupplierId] = useState("");
  const [purchaseInvoiceNo, setPurchaseInvoiceNo] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState("fixed"); // fixed or percent
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentModeType, setPaymentModeType] = useState("full"); // full, partial, credit, split
  const [multiPayments, setMultiPayments] = useState([
    { method: "Cash", amount: "" },
  ]);
  const [receivingNotes, setReceivingNotes] = useState("");
  const [receivingItems, setReceivingItems] = useState([]);

  // Open Approval Modal
  const handleOpenApprovalModal = () => {
    if (!requisition?.items || !Array.isArray(requisition.items)) return;
    const initial = requisition.items.map((item) => ({
      id: item?.id,
      item_title: item?.item_title || "Item",
      requested_quantity: Number(item?.requested_quantity) || 0,
      requested_unit: item?.requested_unit || item?.stock_unit || "",
      current_stock: Number(item?.current_stock) || 0,
      approved_quantity:
        item?.approved_quantity !== null && item?.approved_quantity !== undefined
          ? item.approved_quantity
          : item?.requested_quantity || 0,
      approval_status: item?.approval_status === "pending" ? "approved" : item?.approval_status || "approved",
      rejection_reason: item?.rejection_reason || "",
    }));
    setItemApprovals(initial);
    setApprovalNotes("");
    setApprovalAction("purchase");
    setIsApprovalModalOpen(true);
  };

  const handleApproveAll = () => {
    setItemApprovals(
      itemApprovals.map((it) => ({
        ...it,
        approved_quantity: it.requested_quantity,
        approval_status: "approved",
        rejection_reason: "",
      }))
    );
  };

  const handleRejectAll = () => {
    setItemApprovals(
      itemApprovals.map((it) => ({
        ...it,
        approved_quantity: 0,
        approval_status: "rejected",
        rejection_reason: "Rejected by admin",
      }))
    );
  };

  const handleSubmitApproval = async () => {
    setIsSubmittingAction(true);
    try {
      const payload = {
        notes: approvalNotes,
        approval_action: approvalAction,
        items: itemApprovals.map((it) => ({
          id: it.id,
          approved_quantity: Number(it.approved_quantity),
          approval_status: it.approval_status,
          rejection_reason: it.approval_status === "rejected" ? it.rejection_reason : null,
        })),
      };

      await approveRequisition(id, payload);
      toast.success(
        approvalAction === "issue"
          ? "Requisition approved & stock issued from internal pantry"
          : "Requisition approved for supplier purchase (ready for print slip)"
      );
      setIsApprovalModalOpen(false);
      mutate();
    } catch (err) {
      console.error("Approval error:", err);
      toast.error(err.response?.data?.message || err.message || "Approval failed");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  // Submit Draft
  const handleSubmitDraft = async () => {
    if (!window.confirm("Submit this requisition draft for admin approval?")) return;
    setIsSubmittingAction(true);
    try {
      await submitRequisitionForApproval(id);
      toast.success("Requisition submitted for approval");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to submit");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  // Forward to Provider
  const handleForwardToProvider = async () => {
    const notesPrompt = window.prompt("Enter any notes/delivery instructions for software provider (optional):", "");
    if (notesPrompt === null) return;

    setIsSubmittingAction(true);
    try {
      await forwardRequisitionToProvider(id, notesPrompt);
      toast.success("Forwarded to software provider as supply order");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to forward");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  // Open Receiving Modal
  const handleOpenReceivingModal = () => {
    if (!requisition?.items || !Array.isArray(requisition.items)) return;
    const eligibleItems = requisition.items.filter(
      (it) => it && (Number(it.approved_quantity) > 0 || Number(it.dispatched_quantity) > 0 || Number(it.requested_quantity) > 0)
    );
    const fallbackList = eligibleItems.length > 0 ? eligibleItems : (requisition.items || []).filter(Boolean);
    const initialItems = fallbackList.map((item) => {
      const expectedQty = Number(item.dispatched_quantity || item.approved_quantity || item.requested_quantity) || 0;
      const prevPrice = Number(item.previous_purchase_price) || Number(item.average_unit_cost) || 0;
      const currentPrice = Number(item.unit_price) > 0 ? Number(item.unit_price) : prevPrice;
      return {
        requisition_item_id: item.id,
        inventory_item_id: item.inventory_item_id,
        item_title: item.item_title || "Item",
        expected_quantity: expectedQty,
        received_quantity: expectedQty,
        accepted_quantity: expectedQty,
        rejected_quantity: 0,
        damaged_quantity: 0,
        shortage_quantity: 0,
        rejection_note: "",
        previous_purchase_price: prevPrice,
        unit_price: currentPrice,
        purchase_unit_id: item.purchase_unit_id || null,
        purchase_unit: item.purchase_unit || item.stock_unit || "",
        conversion_factor: Number(item.conversion_factor) || 1,
      };
    });

    setReceivingItems(initialItems);
    setSupplierId(suppliers?.[0]?.id || "");
    setPurchaseInvoiceNo("");
    setPurchaseDate(new Date().toISOString().split("T")[0]);
    setDiscountAmount(0);
    setDiscountType("fixed");
    setPaymentModeType("full");
    const defaultMethod = availablePaymentMethods[0] || "Cash";
    setPaymentMethod(defaultMethod);
    setPaidAmount(0);
    setMultiPayments([{ method: defaultMethod, amount: "" }]);
    setReceivingNotes("");
    setIsReceivingModalOpen(true);
  };

  // Calculations for Receiving Modal with NaN-guards
  const receivingSubtotal = useMemo(() => {
    if (!Array.isArray(receivingItems)) return 0;
    return receivingItems.reduce((acc, it) => {
      const lineTotal = (Number(it?.accepted_quantity) || 0) * (Number(it?.unit_price) || 0);
      return acc + (isNaN(lineTotal) ? 0 : lineTotal);
    }, 0);
  }, [receivingItems]);

  const computedDiscount = useMemo(() => {
    const rawDisc = Number(discountAmount) || 0;
    if (isNaN(rawDisc) || rawDisc < 0) return 0;
    if (discountType === "percent") {
      const pct = (receivingSubtotal * rawDisc) / 100;
      return isNaN(pct) ? 0 : Math.min(receivingSubtotal, Math.max(0, pct));
    }
    return Math.min(receivingSubtotal, rawDisc);
  }, [receivingSubtotal, discountAmount, discountType]);

  const receivingNetTotal = Math.max(0, (receivingSubtotal || 0) - (computedDiscount || 0));

  const totalSplitPaid = useMemo(() => {
    if (paymentModeType === "split") {
      if (!Array.isArray(multiPayments)) return 0;
      return multiPayments.reduce((acc, p) => {
        const amt = Number(p?.amount) || 0;
        return acc + (isNaN(amt) ? 0 : amt);
      }, 0);
    }
    if (paymentModeType === "full") return receivingNetTotal;
    if (paymentModeType === "credit") return 0;
    const paid = Number(paidAmount) || 0;
    return isNaN(paid) ? 0 : paid;
  }, [paymentModeType, multiPayments, receivingNetTotal, paidAmount]);

  const receivingDueAmount = Math.max(0, (receivingNetTotal || 0) - (totalSplitPaid || 0));

  const handleReceivingItemChange = (index, field, value) => {
    if (!receivingItems[index]) return;
    const updated = [...receivingItems];
    updated[index] = { ...updated[index], [field]: value };

    if (field === "received_quantity") {
      const rec = Number(value) || 0;
      updated[index].accepted_quantity = rec;
      updated[index].rejected_quantity = 0;
      updated[index].shortage_quantity = Math.max(0, (Number(updated[index].expected_quantity) || 0) - rec);
    } else if (field === "accepted_quantity") {
      const acc = Number(value) || 0;
      const rec = Number(updated[index].received_quantity) || 0;
      updated[index].rejected_quantity = Math.max(0, rec - acc);
    }

    setReceivingItems(updated);
  };

  const handleAddSplitPaymentRow = () => {
    const defaultMethod = availablePaymentMethods[0] || "Cash";
    setMultiPayments([...multiPayments, { method: defaultMethod, amount: "" }]);
  };

  const handleRemoveSplitPaymentRow = (idx) => {
    if (multiPayments.length <= 1) return;
    setMultiPayments(multiPayments.filter((_, i) => i !== idx));
  };

  const handleSplitPaymentChange = (idx, field, val) => {
    if (!multiPayments[idx]) return;
    const updated = [...multiPayments];
    updated[idx] = { ...updated[idx], [field]: val };
    setMultiPayments(updated);
  };

  const handleConfirmReceipt = async () => {
    if (!supplierId) {
      toast.error("Please select a supplier");
      return;
    }

    const acceptedItems = receivingItems.filter((it) => Number(it.accepted_quantity) > 0);
    if (acceptedItems.length === 0) {
      toast.error("At least one accepted item is required to confirm receipt");
      return;
    }

    // Verify unit prices
    const missingPrice = acceptedItems.some((it) => Number(it.unit_price) <= 0);
    if (missingPrice) {
      if (!window.confirm("Some accepted items have unit price of 0. Do you want to proceed?")) {
        return;
      }
    }

    setIsSubmittingAction(true);
    try {
      const payload = {
        supplier_id: supplierId,
        purchase_invoice_no: purchaseInvoiceNo || null,
        purchase_date: purchaseDate,
        discount_amount: computedDiscount,
        payment_method: paymentMethod,
        notes: receivingNotes || (requisition?.requisition_no ? `Requisition receipt #${requisition.requisition_no}` : "Requisition receipt"),
        items: receivingItems.map((it) => ({
          requisition_item_id: it.requisition_item_id,
          inventory_item_id: it.inventory_item_id,
          received_quantity: Number(it.received_quantity) || 0,
          accepted_quantity: Number(it.accepted_quantity) || 0,
          rejected_quantity: Number(it.rejected_quantity) || 0,
          damaged_quantity: Number(it.damaged_quantity) || 0,
          shortage_quantity: Number(it.shortage_quantity) || 0,
          rejection_note: it.rejection_note || null,
          previous_purchase_price: Number(it.previous_purchase_price) || 0,
          unit_price: Number(it.unit_price) || 0,
          purchase_unit_id: it.purchase_unit_id || null,
          purchase_unit: it.purchase_unit || null,
          conversion_factor: Number(it.conversion_factor) || 1,
        })),
      };

      if (paymentModeType === "split") {
        payload.multi_payments = multiPayments
          .filter((p) => Number(p.amount) > 0)
          .map((p) => ({ method: p.method, amount: Number(p.amount) }));
        payload.paid_amount = totalSplitPaid;
      } else if (paymentModeType === "full") {
        payload.paid_amount = receivingNetTotal;
      } else if (paymentModeType === "credit") {
        payload.paid_amount = 0;
      } else {
        payload.paid_amount = Number(paidAmount) || 0;
      }

      await recordRequisitionReceiving(id, payload);
      toast.success("Receipt confirmed! Inventory stock & purchase invoices updated.");
      setIsReceivingModalOpen(false);
      mutate();
    } catch (err) {
      console.error("Confirm receipt error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to confirm receipt");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  if (isLoading) {
    return (
      <Page className="p-6 text-center">
        <span className="loading loading-spinner loading-lg text-emerald-600"></span>
        <p className="text-sm text-gray-500 mt-2">Loading requisition details...</p>
      </Page>
    );
  }

  if (error || !requisition) {
    return (
      <Page className="p-6">
        <div className="alert alert-error rounded-2xl">
          <IconAlertCircle />
          <span>{error?.message || "Requisition not found"}</span>
        </div>
        <Link to="/dashboard/inventory/requisitions" className="btn btn-sm btn-ghost mt-4">
          ← Back to Requisitions
        </Link>
      </Page>
    );
  }

  const statusInfo = STATUS_CONFIG[requisition.status] || {
    label: requisition.status,
    badge: "badge-ghost",
  };
  const isProvider = requisition.mode === "provider";

  return (
    <Page className="p-4 md:p-6 space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/inventory/requisitions"
            className="btn btn-sm btn-circle btn-ghost"
          >
            <IconArrowLeft size={20} />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{requisition.requisition_no}</h1>
              <span className={`badge ${statusInfo.badge} font-medium`}>
                {statusInfo.label}
              </span>
              {requisition.priority === "urgent" && (
                <span className="badge badge-error badge-sm text-white uppercase font-bold">
                  Urgent
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              Created on {formatDateSafe(requisition.request_date, true)} by{" "}
              <span className="font-medium text-gray-600 dark:text-gray-300">
                {requisition.requested_by_name || requisition.requested_by}
              </span>
            </p>
          </div>
        </div>

        {/* Action Buttons Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Thermal Print Slip Button */}
          <button
            onClick={() => printRequisitionSlip(requisition, storeSettings)}
            className="btn btn-sm btn-outline border-gray-300 dark:border-neutral-600 rounded-xl flex items-center gap-1.5 shadow-sm"
            title="Print Thermal Requisition Slip (80mm/70mm/58mm)"
          >
            <IconPrinter size={16} />
            Print Slip
          </button>

          {requisition.status === "draft" && (
            <button
              onClick={handleSubmitDraft}
              disabled={isSubmittingAction}
              className="btn btn-sm btn-primary bg-emerald-600 hover:bg-emerald-700 text-white border-none rounded-xl"
            >
              <IconSend size={16} />
              Submit for Approval
            </button>
          )}

          {requisition.status === "pending_approval" && (
            <button
              onClick={handleOpenApprovalModal}
              className="btn btn-sm btn-primary bg-emerald-600 hover:bg-emerald-700 text-white border-none rounded-xl shadow-sm"
            >
              <IconCheck size={16} />
              Review & Approve
            </button>
          )}

          {isProvider && ["approved", "partially_approved"].includes(requisition.status) && (
            <button
              onClick={handleForwardToProvider}
              disabled={isSubmittingAction}
              className="btn btn-sm btn-secondary bg-purple-600 hover:bg-purple-700 text-white border-none rounded-xl shadow-sm"
            >
              <IconTruck size={16} />
              Forward to Provider
            </button>
          )}

          {/* Receiving & Confirm Purchase Button (For both Provider and Supplier Purchase Requisitions) */}
          {!requisition.purchase_id && ["approved", "partially_approved", "provider_dispatched", "issued"].includes(requisition.status) && (
            <button
              onClick={handleOpenReceivingModal}
              className="btn btn-sm btn-accent bg-teal-600 hover:bg-teal-700 text-white border-none rounded-xl shadow-sm flex items-center gap-1.5"
            >
              <IconBoxSeam size={16} />
              Record Arrival & Confirm Purchase
            </button>
          )}
        </div>
      </div>

      {/* Mode Banner */}
      <div
        className={`p-4 rounded-2xl border flex items-center justify-between gap-4 ${
          isProvider
            ? "bg-purple-50/60 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800 text-purple-900 dark:text-purple-200"
            : "bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200"
        }`}
      >
        <div className="flex items-center gap-3">
          {isProvider ? (
            <IconTruck className="text-purple-600 shrink-0" size={24} />
          ) : (
            <IconBuildingWarehouse className="text-emerald-600 shrink-0" size={24} />
          )}
          <div>
            <div className="font-semibold text-sm">
              {isProvider ? "Integrated Software Provider Mode" : "Store Requisition & Inventory Flow"}
            </div>
            <div className="text-xs opacity-80 mt-0.5">
              {isProvider
                ? "Items are supplied by the software provider. Stock updates only after ingredients arrive and admin confirms receipt."
                : "Print requisition slips for your storekeeper/supplier. When goods arrive, click 'Record Arrival & Confirm Purchase' with the supplier invoice to ADD stock to inventory."}
            </div>
          </div>
        </div>

        {requisition.purchase ? (
          <Link
            to="/dashboard/inventory/purchases"
            className="btn btn-xs bg-white dark:bg-neutral-800 text-indigo-600 border border-indigo-200 rounded-lg flex items-center gap-1 shadow-sm"
          >
            <IconReceipt size={14} />
            Bill #{requisition.purchase.purchase_invoice_no || requisition.purchase.id}
          </Link>
        ) : null}
      </div>

      {/* Meta Grid Card */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-neutral-700">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <div>
            <span className="text-gray-400 block uppercase font-medium">Department</span>
            <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm mt-0.5 block">
              {requisition.department || "General"}
            </span>
          </div>

          <div>
            <span className="text-gray-400 block uppercase font-medium">Needed By</span>
            <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm mt-0.5 block">
              {requisition.needed_by_date
                ? formatDateSafe(requisition.needed_by_date)
                : "Not specified"}
            </span>
          </div>

          <div>
            <span className="text-gray-400 block uppercase font-medium">Approved By</span>
            <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm mt-0.5 block">
              {requisition.approved_by_name || requisition.approved_by || "Pending"}
            </span>
          </div>

          <div>
            <span className="text-gray-400 block uppercase font-medium">Received Date</span>
            <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm mt-0.5 block">
              {requisition.received_date
                ? formatDateSafe(requisition.received_date)
                : requisition.status === "issued"
                ? "Issued from Store"
                : "Not received yet"}
            </span>
          </div>
        </div>

        {requisition.requester_notes && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-neutral-700 text-xs">
            <span className="text-gray-400 uppercase font-medium block mb-1">Requester Notes</span>
            <p className="text-gray-700 dark:text-gray-300 italic bg-gray-50 dark:bg-neutral-900/40 p-2.5 rounded-xl">
              "{requisition.requester_notes}"
            </p>
          </div>
        )}

        {requisition.approval_notes && (
          <div className="mt-3 text-xs">
            <span className="text-gray-400 uppercase font-medium block mb-1">Admin Approval Notes</span>
            <p className="text-gray-700 dark:text-gray-300 italic bg-amber-50/50 dark:bg-amber-950/20 p-2.5 rounded-xl border border-amber-200/50">
              "{requisition.approval_notes}"
            </p>
          </div>
        )}
      </div>

      {/* Linked Purchase Invoice Summary Card (if received) */}
      {requisition.purchase && (
        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-5 shadow-sm border border-emerald-100 dark:border-emerald-900/40 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <IconReceipt className="text-emerald-600" size={20} />
              <h3 className="font-bold text-sm">Linked Purchase Invoice & Ledger</h3>
            </div>
            <span
              className={`badge badge-sm font-medium ${
                requisition.purchase.payment_status === "paid"
                  ? "badge-success text-white"
                  : requisition.purchase.payment_status === "partial"
                  ? "badge-warning"
                  : "badge-error text-white"
              }`}
            >
              {requisition.purchase.payment_status?.toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs bg-gray-50 dark:bg-neutral-900/50 p-4 rounded-xl">
            <div>
              <span className="text-gray-400 block">Supplier</span>
              <span className="font-semibold text-gray-800 dark:text-gray-100 mt-0.5 block">
                {requisition.purchase.supplier_name || "Supplier"}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block">Bill #</span>
              <span className="font-semibold font-mono text-gray-800 dark:text-gray-100 mt-0.5 block">
                {requisition.purchase.purchase_invoice_no || `#${requisition.purchase.id}`}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block">Total Amount</span>
              <span className="font-semibold text-emerald-600 mt-0.5 block">
                {currency} {formatNumberSafe(requisition.purchase.total_amount)}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block">Paid / Due</span>
              <span className="font-semibold text-gray-800 dark:text-gray-100 mt-0.5 block">
                {currency} {formatNumberSafe(requisition.purchase.paid_amount)} /{" "}
                <span className="text-rose-600 font-bold">
                  {currency} {formatNumberSafe(requisition.purchase.due_amount)}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-700 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
          <h3 className="font-bold text-sm flex items-center gap-2">
            <IconClipboardList size={18} className="text-emerald-600" />
            Requisition Items ({requisition.items?.length || 0})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="table table-zebra w-full text-xs">
            <thead className="bg-gray-50/80 dark:bg-neutral-900/60 uppercase font-semibold text-gray-500">
              <tr>
                <th className="py-3 px-4">Ingredient / Item</th>
                <th className="py-3 px-4 text-right">Requested</th>
                <th className="py-3 px-4 text-right">Approved</th>
                {isProvider && <th className="py-3 px-4 text-right">Dispatched</th>}
                {requisition.status === "received" && (
                  <>
                    <th className="py-3 px-4 text-right">Received</th>
                    <th className="py-3 px-4 text-right text-emerald-600">Accepted</th>
                    <th className="py-3 px-4 text-right text-rose-500">Rej / Damaged</th>
                    <th className="py-3 px-4 text-right">Unit Price</th>
                    <th className="py-3 px-4 text-right">Total</th>
                  </>
                )}
                <th className="py-3 px-4">Status / Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-neutral-700/60">
              {requisition.items?.map((item) => {
                const reqQty = Number(item.requested_quantity) || 0;
                const appQty = item.approved_quantity !== null ? Number(item.approved_quantity) : "—";
                const dispQty = item.dispatched_quantity !== null ? Number(item.dispatched_quantity) : "—";
                const unit = item.requested_unit || item.stock_unit || "";

                return (
                  <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-neutral-700/30">
                    <td className="py-3 px-4">
                      <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                        {item.item_title}
                      </div>
                      {item.notes && <div className="text-xs text-gray-400 italic">"{item.notes}"</div>}
                    </td>

                    <td className="py-3 px-4 text-right font-medium">
                      {reqQty} {unit}
                    </td>

                    <td className="py-3 px-4 text-right font-semibold text-emerald-600">
                      {appQty !== "—" ? `${appQty} ${unit}` : "—"}
                    </td>

                    {isProvider && (
                      <td className="py-3 px-4 text-right font-medium text-purple-600">
                        {dispQty !== "—" ? `${dispQty} ${unit}` : "—"}
                      </td>
                    )}

                    {requisition.status === "received" && (
                      <>
                        <td className="py-3 px-4 text-right font-medium">
                          {item.received_quantity || 0} {unit}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-emerald-600">
                          {item.accepted_quantity || 0} {unit}
                        </td>
                        <td className="py-3 px-4 text-right font-medium text-rose-500">
                          {(Number(item.rejected_quantity) || 0) + (Number(item.damaged_quantity) || 0)}{" "}
                          {unit}
                        </td>
                        <td className="py-3 px-4 text-right font-mono">
                          {currency} {formatNumberSafe(item.unit_price)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono font-semibold">
                          {currency} {formatNumberSafe(item.total_price)}
                        </td>
                      </>
                    )}

                    <td className="py-3 px-4">
                      {item.approval_status === "rejected" ? (
                        <span className="badge badge-error badge-xs text-white">
                          Rejected {item.rejection_reason ? `(${item.rejection_reason})` : ""}
                        </span>
                      ) : item.approval_status === "partial" ? (
                        <span className="badge badge-info badge-xs">Partial</span>
                      ) : item.approval_status === "approved" ? (
                        <span className="badge badge-success badge-xs text-white">Approved</span>
                      ) : (
                        <span className="badge badge-ghost badge-xs">Pending</span>
                      )}
                      {item.rejection_note && (
                        <div className="text-[10px] text-rose-500 mt-0.5">Note: {item.rejection_note}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status History & Audit Trail */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-neutral-700">
        <h3 className="font-bold text-sm mb-4 flex items-center gap-2">
          <IconClock size={18} className="text-emerald-600" />
          Audit Trail & Workflow History
        </h3>

        <div className="space-y-4">
          {requisition.history?.map((hist, idx) => (
            <div key={hist.id || idx} className="flex items-start gap-3 text-xs">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1 shrink-0"></div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {hist.changed_by_name || hist.changed_by}
                  </span>
                  <span className="badge badge-xs badge-ghost uppercase">{hist.to_status}</span>
                  <span className="text-gray-400">
                    {formatDateSafe(hist.created_at, true)}
                  </span>
                </div>
                {hist.notes && <p className="text-gray-500 mt-0.5">{hist.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* Admin Approval Modal */}
      {/* ────────────────────────────────────────────────────────────── */}
      {isApprovalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-neutral-800 rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl border border-gray-100 dark:border-neutral-700 animate-in fade-in my-8">
            <div className="p-6 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <IconCheck className="text-emerald-600" />
                  Review & Approve Requisition
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Adjust approved quantities and approve or reject requested ingredients
                </p>
              </div>
              <button
                onClick={() => setIsApprovalModalOpen(false)}
                className="btn btn-sm btn-ghost btn-circle"
              >
                <IconX size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Quick Actions */}
              <div className="flex items-center justify-between bg-gray-50 dark:bg-neutral-900/50 p-3 rounded-xl text-xs">
                <span className="text-gray-500">Quickly apply status to all items:</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleApproveAll}
                    className="btn btn-xs btn-outline border-emerald-600 text-emerald-600 rounded-lg"
                  >
                    Approve All
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="btn btn-xs btn-outline border-rose-500 text-rose-500 rounded-lg"
                  >
                    Reject All
                  </button>
                </div>
              </div>

              {/* Items Decision Table */}
              <div className="space-y-3">
                {itemApprovals.map((it, idx) => (
                  <div
                    key={it.id}
                    className={`p-4 rounded-2xl border transition-all ${
                      it.approval_status === "rejected"
                        ? "bg-rose-50/40 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800"
                        : "bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700"
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-sm">{it.item_title}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          Requested: <span className="font-bold">{it.requested_quantity} {it.requested_unit}</span> | Stock on hand: {it.current_stock}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <select
                          value={it.approval_status}
                          onChange={(e) => {
                            const updated = [...itemApprovals];
                            updated[idx].approval_status = e.target.value;
                            if (e.target.value === "rejected") {
                              updated[idx].approved_quantity = 0;
                            } else if (e.target.value === "approved") {
                              updated[idx].approved_quantity = it.requested_quantity;
                            }
                            setItemApprovals(updated);
                          }}
                          className="select select-bordered select-xs rounded-lg"
                        >
                          <option value="approved">Approve</option>
                          <option value="partial">Partial</option>
                          <option value="rejected">Reject</option>
                        </select>

                        {it.approval_status !== "rejected" && (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={it.requested_quantity}
                              value={it.approved_quantity}
                              onChange={(e) => {
                                const updated = [...itemApprovals];
                                updated[idx].approved_quantity = e.target.value;
                                setItemApprovals(updated);
                              }}
                              className="input input-bordered input-xs w-20 rounded-lg text-right font-medium"
                            />
                            <span className="text-xs text-gray-400">{it.requested_unit}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {it.approval_status === "rejected" && (
                      <div className="mt-2">
                        <input
                          type="text"
                          placeholder="Rejection reason (e.g. Over budget, sufficient stock)..."
                          value={it.rejection_reason}
                          onChange={(e) => {
                            const updated = [...itemApprovals];
                            updated[idx].rejection_reason = e.target.value;
                            setItemApprovals(updated);
                          }}
                          className="input input-bordered input-xs w-full rounded-lg text-xs"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Fulfillment Stock Action Selector */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                  Requisition Fulfillment Type:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    onClick={() => setApprovalAction("purchase")}
                    className={`p-3.5 rounded-2xl border-2 cursor-pointer transition-all ${
                      approvalAction === "purchase"
                        ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20"
                        : "border-gray-200 dark:border-neutral-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="approval_action"
                        checked={approvalAction === "purchase"}
                        onChange={() => setApprovalAction("purchase")}
                        className="radio radio-success radio-sm"
                      />
                      <div className="font-semibold text-xs text-gray-800 dark:text-gray-100">
                        Purchase from Supplier / Storekeeper
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 pl-6">
                      Stock is <strong>NOT</strong> deducted now. Print the thermal slip for the storekeeper/supplier. When items arrive with an invoice, record arrival to <strong>ADD</strong> stock.
                    </p>
                  </div>

                  <div
                    onClick={() => setApprovalAction("issue")}
                    className={`p-3.5 rounded-2xl border-2 cursor-pointer transition-all ${
                      approvalAction === "issue"
                        ? "border-amber-500 bg-amber-50/50 dark:bg-amber-950/20"
                        : "border-gray-200 dark:border-neutral-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="approval_action"
                        checked={approvalAction === "issue"}
                        onChange={() => setApprovalAction("issue")}
                        className="radio radio-warning radio-sm"
                      />
                      <div className="font-semibold text-xs text-gray-800 dark:text-gray-100">
                        Issue from Internal Pantry
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 pl-6">
                      Items exist in current kitchen store. Approving will <strong>DEDUCT</strong> stock immediately from inventory.
                    </p>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                  Admin Approval Notes
                </label>
                <textarea
                  rows={2}
                  placeholder="Notes for kitchen/requester..."
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  className="textarea textarea-bordered w-full rounded-xl text-sm"
                />
              </div>

              {approvalAction === "issue" ? (
                <div className="alert alert-warning text-xs rounded-xl">
                  <IconInfoCircle />
                  <span>
                    <strong>Internal Store Issue:</strong> Confirming approval will immediately deduct approved quantities from your restaurant's internal inventory.
                  </span>
                </div>
              ) : (
                <div className="alert alert-info text-xs rounded-xl bg-blue-50/50 dark:bg-blue-950/20 text-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-800">
                  <IconInfoCircle className="text-blue-600" />
                  <span>
                    <strong>Supplier Purchase Requisition:</strong> Inventory stock will <strong>NOT</strong> be deducted now. After approval, you can print the thermal requisition slip and later click "Record Arrival & Confirm Purchase" to enter the supplier bill and <strong>ADD</strong> stock.
                  </span>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 dark:border-neutral-700 flex items-center justify-between bg-gray-50/50 dark:bg-neutral-900/30">
              <button
                type="button"
                onClick={() => setIsApprovalModalOpen(false)}
                className="btn btn-sm btn-ghost rounded-xl"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingAction}
                onClick={handleSubmitApproval}
                className="btn btn-sm btn-primary bg-emerald-600 hover:bg-emerald-700 text-white border-none rounded-xl shadow-sm flex items-center gap-1.5"
              >
                {isSubmittingAction ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <IconCheck size={16} />
                )}
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* Receiving & Purchase Entry Modal */}
      {/* ────────────────────────────────────────────────────────────── */}
      {isReceivingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-neutral-800 rounded-3xl w-full max-w-5xl max-h-[94vh] flex flex-col shadow-2xl border border-gray-100 dark:border-neutral-700 animate-in fade-in my-6">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <IconBoxSeam className="text-teal-600" />
                  Record Arrival & Purchase Entry
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Inspect incoming quantities, enter supplier invoice details, record payments, and update stock
                </p>
              </div>
              <button
                onClick={() => setIsReceivingModalOpen(false)}
                className="btn btn-sm btn-ghost btn-circle"
              >
                <IconX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Supplier & Invoice Header Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50 dark:bg-neutral-900/40 p-4 rounded-2xl border border-gray-100 dark:border-neutral-700">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                    Supplier *
                  </label>
                  <select
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                    className="select select-bordered select-sm w-full rounded-xl bg-white dark:bg-neutral-800 text-sm"
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                    Invoice / Bill # *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. INV-2026-0812"
                    value={purchaseInvoiceNo}
                    onChange={(e) => setPurchaseInvoiceNo(e.target.value)}
                    className="input input-bordered input-sm w-full rounded-xl bg-white dark:bg-neutral-800 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                    Receiving Date *
                  </label>
                  <input
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    className="input input-bordered input-sm w-full rounded-xl bg-white dark:bg-neutral-800 text-sm"
                  />
                </div>
              </div>

              {/* Items Inspection Table */}
              <div className="space-y-3">
                <label className="text-xs font-semibold uppercase text-gray-500 block">
                  Delivered Ingredients & Quality Inspection
                </label>

                <div className="overflow-x-auto border border-gray-200 dark:border-neutral-700 rounded-2xl">
                  <table className="table table-compact w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-neutral-900/60 uppercase">
                      <tr>
                        <th className="py-2.5 px-3">Ingredient</th>
                        <th className="py-2.5 px-2 text-right">Expected</th>
                        <th className="py-2.5 px-2 text-right">Delivered</th>
                        <th className="py-2.5 px-2 text-right text-emerald-600">Accepted *</th>
                        <th className="py-2.5 px-2 text-right text-rose-500">Rejected</th>
                        <th className="py-2.5 px-2 text-right text-amber-500">Damaged</th>
                        <th className="py-2.5 px-2 text-right text-indigo-600 dark:text-indigo-400">Prev Price ({currency})</th>
                        <th className="py-2.5 px-2 text-right text-emerald-700 dark:text-emerald-400">Unit Price ({currency}) *</th>
                        <th className="py-2.5 px-3 text-right">Total ({currency})</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                      {receivingItems.map((it, idx) => {
                        const lineTotal = (Number(it.accepted_quantity) || 0) * (Number(it.unit_price) || 0);

                        return (
                          <tr key={it.requisition_item_id || idx} className="hover:bg-gray-50/50">
                            <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-100">
                              {it.item_title}
                              <input
                                type="text"
                                placeholder="Rejection notes / condition..."
                                value={it.rejection_note}
                                onChange={(e) => handleReceivingItemChange(idx, "rejection_note", e.target.value)}
                                className="input input-ghost input-xs w-full text-[10px] text-gray-400 mt-0.5 p-0 focus:text-gray-700"
                              />
                            </td>

                            <td className="py-2 px-2 text-right font-medium text-gray-500">
                              {it.expected_quantity}
                            </td>

                            <td className="py-2 px-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={it.received_quantity}
                                onChange={(e) => handleReceivingItemChange(idx, "received_quantity", e.target.value)}
                                className="input input-bordered input-xs w-16 text-right rounded-lg"
                              />
                            </td>

                            <td className="py-2 px-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={it.accepted_quantity}
                                onChange={(e) => handleReceivingItemChange(idx, "accepted_quantity", e.target.value)}
                                className="input input-bordered input-xs w-16 text-right font-bold text-emerald-600 rounded-lg"
                              />
                            </td>

                            <td className="py-2 px-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={it.rejected_quantity}
                                onChange={(e) => handleReceivingItemChange(idx, "rejected_quantity", e.target.value)}
                                className="input input-bordered input-xs w-14 text-right text-rose-500 rounded-lg"
                              />
                            </td>

                            <td className="py-2 px-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={it.damaged_quantity}
                                onChange={(e) => handleReceivingItemChange(idx, "damaged_quantity", e.target.value)}
                                className="input input-bordered input-xs w-14 text-right text-amber-500 rounded-lg"
                              />
                            </td>

                            {/* Previous Purchase Price */}
                            <td className="py-2 px-2 text-right font-mono text-gray-600 dark:text-gray-300">
                              {Number(it.previous_purchase_price) > 0 ? (
                                <span className="badge badge-ghost badge-sm font-mono font-bold text-gray-700 dark:text-gray-200">
                                  {formatNumberSafe(it.previous_purchase_price)}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>

                            {/* Current Unit Price */}
                            <td className="py-2 px-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={it.unit_price}
                                onChange={(e) => handleReceivingItemChange(idx, "unit_price", e.target.value)}
                                className="input input-bordered input-xs w-20 text-right font-mono font-bold rounded-lg"
                              />
                            </td>

                            <td className="py-2 px-3 text-right font-mono font-bold text-emerald-600">
                              {formatNumberSafe(lineTotal)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Financial Calculation, Discount & Payment Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 dark:bg-neutral-900/40 p-5 rounded-2xl border border-gray-200 dark:border-neutral-700">
                {/* Left Column: Dedicated Discount & Payment Method */}
                <div className="space-y-4">
                  {/* 1. Dedicated Invoice Discount Section */}
                  <div className="bg-white dark:bg-neutral-800 p-4 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm space-y-2.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide flex items-center gap-1.5">
                        <IconReceipt size={16} className="text-emerald-600" />
                        Purchase Invoice Discount
                      </label>
                      <span className="text-xs font-semibold text-emerald-600">
                        {computedDiscount > 0
                          ? `- ${currency} ${formatNumberSafe(computedDiscount)} off`
                          : "No discount"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div>
                        <label className="text-[11px] font-medium text-gray-500 block mb-1">Discount Type</label>
                        <div className="grid grid-cols-2 gap-1 bg-gray-100 dark:bg-neutral-900 p-1 rounded-xl text-xs font-semibold">
                          <button
                            type="button"
                            onClick={() => setDiscountType("fixed")}
                            className={`py-1 rounded-lg text-center transition-all ${
                              discountType === "fixed"
                                ? "bg-white dark:bg-neutral-700 text-emerald-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-800"
                            }`}
                          >
                            Fixed ({currency})
                          </button>
                          <button
                            type="button"
                            onClick={() => setDiscountType("percent")}
                            className={`py-1 rounded-lg text-center transition-all ${
                              discountType === "percent"
                                ? "bg-white dark:bg-neutral-700 text-emerald-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-800"
                            }`}
                          >
                            Percent (%)
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="text-[11px] font-medium text-gray-500 block mb-1">
                          Discount Value {discountType === "percent" ? "(%)" : `(${currency})`}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={discountAmount === 0 ? "" : discountAmount}
                          onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                          className="input input-bordered input-sm w-full text-right font-mono font-bold rounded-xl text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 2. Dedicated Payment Section */}
                  <div className="bg-white dark:bg-neutral-800 p-4 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm space-y-3">
                    <div>
                      <label className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide block mb-2">
                        Payment Mode
                      </label>
                      <div className="grid grid-cols-4 gap-1.5 bg-gray-100 dark:bg-neutral-900 p-1 rounded-xl text-xs font-medium">
                        {["full", "partial", "credit", "split"].map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setPaymentModeType(m)}
                            className={`py-1.5 rounded-lg capitalize font-semibold transition-all ${
                              paymentModeType === m
                                ? "bg-white dark:bg-neutral-700 text-emerald-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-800"
                            }`}
                          >
                            {m === "credit" ? "Due / Credit" : m}
                          </button>
                        ))}
                      </div>
                    </div>

                    {paymentModeType === "split" ? (
                      <div className="space-y-2 pt-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            Split Across Payment Types:
                          </span>
                          <button
                            type="button"
                            onClick={handleAddSplitPaymentRow}
                            className="btn btn-xs btn-outline border-emerald-600 text-emerald-600 rounded-lg"
                          >
                            + Add Line
                          </button>
                        </div>

                        <div className="space-y-2">
                          {multiPayments.map((p, pIdx) => (
                            <div key={pIdx} className="flex items-center gap-2">
                              <select
                                value={p.method}
                                onChange={(e) => handleSplitPaymentChange(pIdx, "method", e.target.value)}
                                className="select select-bordered select-sm flex-1 rounded-xl text-xs font-semibold text-gray-800 dark:text-gray-100"
                              >
                                {availablePaymentMethods.map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>

                              <input
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={p.amount}
                                onChange={(e) => handleSplitPaymentChange(pIdx, "amount", e.target.value)}
                                className="input input-bordered input-sm w-32 text-right font-mono font-bold rounded-xl text-xs"
                              />

                              <button
                                type="button"
                                onClick={() => handleRemoveSplitPaymentRow(pIdx)}
                                className="btn btn-xs btn-ghost btn-circle text-rose-500"
                              >
                                <IconX size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : paymentModeType === "credit" ? (
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50 rounded-xl text-xs text-amber-700 dark:text-amber-300">
                        Total amount of <strong>{currency} {formatNumberSafe(receivingNetTotal)}</strong> will be credited to supplier's Accounts Payable ledger as outstanding balance due.
                      </div>
                    ) : (
                      <div className={paymentModeType === "partial" ? "grid grid-cols-2 gap-3 pt-1" : "space-y-3 pt-1"}>
                        <div>
                          <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                            Payment Method (From Settings)
                          </label>
                          <select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="select select-bordered select-sm w-full rounded-xl text-xs font-semibold text-gray-800 dark:text-gray-100"
                          >
                            {availablePaymentMethods.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>

                        {paymentModeType === "partial" && (
                          <div>
                            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                              Amount Paid ({currency})
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={paidAmount}
                              onChange={(e) => setPaidAmount(e.target.value)}
                              className="input input-bordered input-sm w-full text-right font-mono rounded-xl font-bold text-emerald-600"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">Payment Reference & Notes</label>
                      <input
                        type="text"
                        placeholder="Cheque #, transaction ref, remarks..."
                        value={receivingNotes}
                        onChange={(e) => setReceivingNotes(e.target.value)}
                        className="input input-bordered input-sm w-full rounded-xl text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column: Complete Invoice Summary Breakdown */}
                <div className="bg-white dark:bg-neutral-800 p-5 rounded-2xl border border-gray-200 dark:border-neutral-700 flex flex-col justify-between shadow-sm">
                  <div className="space-y-3">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100 dark:border-neutral-700 pb-2">
                      Invoice Summary Breakdown
                    </h4>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Gross Subtotal:</span>
                      <span className="font-mono font-semibold text-gray-800 dark:text-gray-100">
                        {currency} {formatNumberSafe(receivingSubtotal)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 flex items-center gap-1">
                        Discount {discountAmount > 0 ? (discountType === "percent" ? `(${discountAmount}%)` : `(Fixed)`) : ""}:
                      </span>
                      <span className="font-mono font-semibold text-rose-500">
                        - {currency} {formatNumberSafe(computedDiscount)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-base font-bold pt-3 border-t border-gray-100 dark:border-neutral-700">
                      <span className="text-gray-800 dark:text-gray-100">Net Total Payable:</span>
                      <span className="text-emerald-600 font-mono text-lg">
                        {currency} {formatNumberSafe(receivingNetTotal)}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2.5 pt-4 border-t border-gray-100 dark:border-neutral-700">
                    <div className="flex items-center justify-between text-sm font-medium">
                      <span className="text-gray-500">Amount Paid Now:</span>
                      <span className="font-mono text-emerald-600 font-bold">
                        {currency} {formatNumberSafe(totalSplitPaid)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between font-bold text-base bg-rose-50 dark:bg-rose-950/20 p-3.5 rounded-xl border border-rose-100 dark:border-rose-900">
                      <span className="text-rose-700 dark:text-rose-300">Supplier Due (Payable):</span>
                      <span className="font-mono text-rose-600 text-lg">
                        {currency} {formatNumberSafe(receivingDueAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock update warning */}
              <div className="alert alert-info text-xs rounded-xl">
                <IconInfoCircle />
                <span>
                  <strong>Strict Stock Protection:</strong> Clicking "Confirm Receipt" will permanently create a Purchase Invoice, log stock movements, increase inventory quantities for accepted items, and post double-entry journal entries. Once confirmed, this cannot be submitted again.
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 dark:border-neutral-700 flex items-center justify-between bg-gray-50/50 dark:bg-neutral-900/30">
              <button
                type="button"
                onClick={() => setIsReceivingModalOpen(false)}
                className="btn btn-sm btn-ghost rounded-xl"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={isSubmittingAction}
                onClick={handleConfirmReceipt}
                className="btn btn-sm btn-primary bg-emerald-600 hover:bg-emerald-700 text-white border-none rounded-xl shadow-sm flex items-center gap-1.5"
              >
                {isSubmittingAction ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <IconCheck size={16} />
                )}
                Confirm Receipt & Update Inventory
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
